/**
 * Provides a PostHog context and hook for accessing the PostHog instance.
 * https://posthog.com/docs/libraries/react
 * https://github.com/PostHog/posthog-js/issues/908
 */

import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import {
  POSTHOG_API_HOST,
  VITE_POSTHOG_API_KEY,
} from "@features/posthog/utils/constants";
import posthog, { PostHogConfig } from "posthog-js";

type PostHogType = typeof posthog | undefined;

const PostHogContext = createContext<PostHogType>(undefined);

const posthogOptions: Partial<PostHogConfig> = {
  api_host: POSTHOG_API_HOST,
  defaults: "2025-05-24",
};

const PostHogProvider = ({ children }: PropsWithChildren) => {
  const [posthogInstance, setPosthogInstance] = useState<PostHogType>();
  const { userInfo, loginId, displayName, userType, userStatus } =
    useUserInfo();

  useEffect(() => {
    if (!VITE_POSTHOG_API_KEY && !POSTHOG_API_HOST) return;

    const instance = posthog.init(VITE_POSTHOG_API_KEY, posthogOptions);
    setPosthogInstance(instance);

    if (userInfo && instance) {
      instance.reset();
      instance.identify(loginId, {
        email: loginId,
        name: displayName,
        userType: userType,
        reguserstatus: userStatus,
        mysphw: userInfo?.visitorId,
      });
    }
  }, [userInfo, loginId, displayName, userType, userStatus]);

  return (
    <PostHogContext.Provider value={posthogInstance}>
      {children}
    </PostHogContext.Provider>
  );
};

const usePostHog = () => useContext(PostHogContext);

export { PostHogProvider, usePostHog };
